import {
  Alert,
  Flex,
  FlexItem,
  FormAlert,
  Pagination,
  PaginationVariant,
} from '@patternfly/react-core'
import { PlayIcon, PauseIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'

import {
  useDomainEvents,
  useInterval,
} from '@app/domain/domain-events/domain-events.hooks'
import { useHandleCurrentPageChanged } from '@app/lib/pagination'
import type { AppState } from '@app/state'

import { DomainEventsFilter } from './domain-events/domain_events_filter'
import DomainEventsTable from './domain-events/domain_events_table'

const initialRecordsPerPage = 50

const DomainEventScreen = function () {
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [paused, setPaused] = useState<boolean>(false)
  const [pausedTime, setPausedTime] = useState<Date>(null)

  const [recordsPerPage, setRecordsPerPage] = useState<number>(
    initialRecordsPerPage,
  )

  const [sources, setSources] = useState<string[]>([])
  const [subjects, setSubjects] = useState<string[]>([])
  const [types, setTypes] = useState<string[]>([])

  const { domainEvents, totalNumberOfRecords, refresh } = useDomainEvents()

  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {},
  )

  const errorMessage = useSelector<AppState, string>(
    (state) => state.domainEvents.errorMessage,
  )

  const onSetPage = useCallback(
    (pageNumber) => {
      setPageNumber(pageNumber)
      refresh(pageNumber, recordsPerPage, pausedTime, sources, subjects, types)
    },
    [recordsPerPage, pausedTime, refresh, sources, subjects, types],
  )

  const onPerPageSelect = useCallback(
    (perPage) => {
      setPageNumber(1)
      setRecordsPerPage(perPage)
      refresh(pageNumber, perPage, pausedTime, sources, subjects, types)
    },
    [pageNumber, pausedTime, refresh, sources, subjects, types],
  )

  const handleExpansionToggle = useCallback(
    (domainEventId: string): void => {
      const newExpandedRows = {
        ...expandedRows,
        [domainEventId]: !expandedRows[domainEventId],
      }
      setExpandedRows(newExpandedRows)
    },
    [expandedRows],
  )

  const handlePauseToggle = useCallback((): void => {
    const newPaused = !paused
    setPaused(newPaused)
    if (newPaused) {
      setPausedTime(new Date())
    } else {
      setPausedTime(null)
    }
  }, [paused, setPausedTime])

  const refreshAll = useCallback(() => {
    refresh(pageNumber, recordsPerPage, pausedTime, sources, subjects, types)
  }, [
    refresh,
    pageNumber,
    recordsPerPage,
    pausedTime,
    sources,
    subjects,
    types,
  ])

  useInterval(refreshAll, 1000)

  const handleSourceChanged = useCallback((updatedSources) => {
    setPageNumber(1)
    setSources(updatedSources)
  }, [])

  const handleSubjectChanged = useCallback((updatedSubjects) => {
    setPageNumber(1)
    setSubjects(updatedSubjects)
  }, [])

  const handleTypeChanged = useCallback((updatedTypes) => {
    setPageNumber(1)
    setTypes(updatedTypes)
  }, [])

  const handleSetPage = useCallback(
    (_event, updatedPageNumber) => {
      onSetPage(updatedPageNumber)
    },
    [onSetPage],
  )

  const handlePerPageSelect = useCallback(
    (_event, updatedPerPage) => {
      onPerPageSelect(updatedPerPage)
    },
    [onPerPageSelect],
  )

  const { handleCurrentPageChanged } = useHandleCurrentPageChanged(
    onSetPage,
    recordsPerPage,
    totalNumberOfRecords,
  )

  return (
    <Flex>
      <FlexItem style={{ width: '-webkit-fill-available' }}>
        <DomainEventsFilter
          onSourceChanged={handleSourceChanged}
          onSubjectChanged={handleSubjectChanged}
          onTypeChanged={handleTypeChanged}
        />
        {errorMessage ? (
          <FormAlert>
            <Alert
              variant="danger"
              title={errorMessage}
              aria-live="polite"
              isInline
            />
          </FormAlert>
        ) : null}

        <DomainEventsTable
          domainEvents={domainEvents}
          expandedRows={expandedRows}
          handleExpanded={handleExpansionToggle}
        />

        <Pagination
          itemCount={totalNumberOfRecords}
          onBlur={handleCurrentPageChanged}
          onChange={handleCurrentPageChanged}
          onPerPageSelect={handlePerPageSelect}
          onSetPage={handleSetPage}
          page={pageNumber}
          perPage={recordsPerPage}
          variant={PaginationVariant.bottom}
          widgetId="pagination-options-menu-bottom"
        >
          <button onClick={handlePauseToggle}>
            {paused ? <PlayIcon /> : <PauseIcon />}
          </button>
        </Pagination>
      </FlexItem>
    </Flex>
  )
}

export default DomainEventScreen
